/**
 * /quiz-buzzer — the host's screen: the room, the referee, the scoreboard.
 *
 * Players reach it two ways, in any mix:
 *   · phones — a room over __RoomConnect (room-connect.js, host-N data
 *     channels). Every phone stamps its press on its own clock; the host keeps
 *     a clock sync per phone (clock.js) and translates the stamp;
 *   · this device — keys, gamepads, USB buzzers, or touch zones on one screen
 *     (inputs.js); those stamps are on the host clock already.
 *
 * The engine (engine.js) is the referee; this file only feeds it, schedules
 * its deadlines, plays its sounds and draws it.
 */
import { createEngine, PRESETS } from './engine.js';
import { createClockSync, correctPress, graceFor, leadFor } from './clock.js';
import * as snd from './sound.js';
import { t, $, el, secs, msec, num, clock, store, load, toast, stage, diag, funnel, rid, cleanName, PALETTE, inkOn, editable, evStamp, CFG, PREFIX, PROTO } from './util.js';
import { drawQrWhenReady } from './qr.js';
import { createLocalInput, keyLabel, DEFAULT_KEYS, HOST_KEYS } from './inputs.js';

const MAX_PLAYERS = 60;
const TEAM_COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#14b8a6', '#ec4899', '#f97316'];

export function startHost(opts) {
    const RC = window.__RoomConnect;
    const root = $('qbHost');
    const H = {
        mode: opts.mode, code: null, room: null, conns: new Set(), syncs: new Map(), boots: new Map(),
        kicked: new Set(), simultaneous: true, present: false, completed: false, ended: false,
        dlTimer: 0, raf: 0, lastTickSec: null, pushQueued: false, renderQueued: false, persistTimer: 0,
        learning: null, zones: false
    };
    const eng = createEngine({ grace: function () { return graceFor(remoteSyncs()); }, now: performance.now() });

    // ── restore or start fresh ──────────────────────────────────────────────
    const saved = opts.resume;
    if (saved && saved.game) {
        eng.fromJSON(saved.game);
        H.simultaneous = saved.simultaneous !== false;
        if (saved.volume != null) snd.setVolume(saved.volume);
        if (saved.muted) snd.setMuted(true);
    } else {
        eng.applyPreset(opts.preset || 'jeopardy');
        const last = load('rules', null);
        if (last && last.preset === (opts.preset || 'jeopardy')) eng.configure(last);
        if (eng.settings.scoring === 'teams') eng.setTeams(defaultTeams(2));
        if (H.mode === 'local') { addLocalPlayer(); addLocalPlayer(); }
    }

    function defaultTeams(n) {
        const out = [];
        for (let i = 0; i < n; i++) out.push({ id: rid(3), name: t('team_n', { n: i + 1 }), color: TEAM_COLORS[i % TEAM_COLORS.length] });
        return out;
    }

    function remoteSyncs() {
        const out = [];
        eng.players.forEach(function (p, pid) {
            if (p.kind === 'remote' && p.online !== false) out.push(H.syncs.get(pid) || createClockSync());
        });
        return out;
    }
    function leadNow() { return H.simultaneous ? leadFor(remoteSyncs()) : 0; }

    function colorIndex(pid) {
        const p = eng.players.get(pid);
        const i = p ? PALETTE.indexOf(p.color) : -1;
        return i >= 0 ? i : (pid ? pid.charCodeAt(0) : 0);
    }
    function usedColors() { const u = []; eng.players.forEach(function (p) { u.push(p.color); }); return u; }
    function pickColor() {
        const used = usedColors();
        for (const c of PALETTE) if (!used.includes(c)) return c;
        return PALETTE[eng.players.size % PALETTE.length];
    }
    function validTeam(id) { return id && eng.teams.some(function (x) { return x.id === id; }) ? id : null; }
    function smallestTeam() {
        const tm = eng.teams;
        if (!tm.length) return null;
        const n = new Map(tm.map(function (x) { return [x.id, 0]; }));
        eng.players.forEach(function (p) { if (n.has(p.team)) n.set(p.team, n.get(p.team) + 1); });
        let best = tm[0].id;
        n.forEach(function (v, k) { if (v < n.get(best)) best = k; });
        return best;
    }

    // ── local players (keys / pads / touch) ─────────────────────────────────
    function localPlayers() {
        const out = [];
        eng.players.forEach(function (p) { if (p.kind === 'key') out.push(p); });
        return out;
    }
    function addLocalPlayer() {
        const locals = localPlayers();
        const usedKeys = locals.map(function (p) { return p.key; });
        const key = DEFAULT_KEYS.find(function (k) { return !usedKeys.includes(k); }) || null;
        const n = locals.length + 1;
        const team = eng.settings.scoring === 'teams' ? smallestTeam() : null;
        return eng.addPlayer({ pid: 'L' + rid(4), name: t('player_n', { n: n }), color: pickColor(), kind: 'key', key: key, team: team, online: true });
    }

    const input = createLocalInput({
        bindings: function () {
            const out = [];
            localPlayers().forEach(function (p) {
                if (!p.key) return;
                if (String(p.key).indexOf('pad:') === 0) out.push({ pid: p.pid, pad: String(p.key).slice(4) });
                else out.push({ pid: p.pid, key: p.key });
            });
            return out;
        },
        onPress: function (pid, ts, src) { localPress(pid, ts, src); }
    });
    const localSeq = new Map();
    function localPress(pid, ts, src) {
        const s = (localSeq.get(pid) || 0) + 1;
        localSeq.set(pid, s);
        snd.unlock();
        eng.press({ pid: pid, t: ts, err: 0, how: src || 'local', seq: s }, performance.now());
    }

    // ── engine events → sound, network, screen ──────────────────────────────
    eng.on(function (type, d) {
        switch (type) {
            case 'armed':
                snd.play('open', { at: d.armAt });
                if (!H.armedOnce) { H.armedOnce = true; stage('armed'); }
                break;
            case 'declared': snd.play('buzz', { i: colorIndex(d.pid) }); break;
            case 'press': if (d.kind === 'false') snd.play('falseStart'); break;
            case 'judged':
                snd.play(d.verdict === 'correct' ? 'correct' : 'wrong');
                fxTo(d.ent, { t: 'fx', k: d.verdict, d: d.delta });
                completeOnce();
                break;
            case 'warn': snd.play('warn'); break;
            case 'time_up': case 'answer_time_up': case 'final': snd.play('timeUp'); break;
            default: break;
        }
        scheduleRender();
        pushStates();
        armDeadline();
        persistSoon();
        if (type === 'settings') store('rules', eng.settings);
    });

    function completeOnce() {
        if (H.completed) return;
        H.completed = true;
        stage('result');
        try {
            if (window.__aeToolComplete) window.__aeToolComplete({
                kind: 'judged', mode: H.mode, preset: eng.settings.preset, scoring: eng.settings.scoring,
                phones: remoteCount(), local: localPlayers().length
            });
        } catch (e) { /* telemetry is optional */ }
    }
    function remoteCount() { let n = 0; eng.players.forEach(function (p) { if (p.kind === 'remote') n++; }); return n; }

    function armDeadline() {
        clearTimeout(H.dlTimer);
        const dl = eng.nextDeadline();
        if (dl == null) return;
        H.dlTimer = setTimeout(function () {
            eng.tick(performance.now());
            armDeadline();
            scheduleRender();
        }, Math.max(0, dl - performance.now()) + 1);
    }

    // ── network: room, handshake, clock sync, presses ───────────────────────
    function send(rec, msg) { try { if (rec.conn && rec.conn.open) rec.conn.send(msg); } catch (e) { /* the close handler cleans up */ } }
    function connOf(pid) { let r = null; H.conns.forEach(function (x) { if (x.pid === pid) r = x; }); return r; }

    function roomState(kind, text) {
        const box = $('qbRoomState');
        if (!box) return;
        box.className = 'tc-msg qb-room-state ' + (kind === 'ok' ? 'good' : kind === 'err' ? 'bad' : 'warn');
        box.textContent = text;
        box.hidden = false;
    }

    function inviteUrl(code) {
        const base = (CFG.base || (location.origin + location.pathname)) + '?room=' + encodeURIComponent(code);
        return typeof window.__withRef === 'function' ? window.__withRef(base) : base;
    }

    function showCode() {
        const url = inviteUrl(H.code);
        $('qbCode').textContent = H.code.toUpperCase();
        $('qbPresentCode').textContent = t('present_join', { code: H.code.toUpperCase() });
        $('qbLink').value = url;
        drawQrWhenReady($('qbQr'), url, 200, t('qr_label'));
        drawQrWhenReady($('qbPresentQr'), url, 120, t('qr_label'));
    }

    function openRoom(code, resuming) {
        if (!RC || typeof window.Peer === 'undefined') {
            roomState('err', t('net_missing'));
            diag('peerjs_missing', {});
            return;
        }
        H.code = code;
        showCode();
        roomState('wait', t('room_opening'));
        let tries = 0;
        const make = function () {
            H.room = RC.hostRoom({
                prefix: PREFIX, code: H.code, tool: 'quiz-buzzer', maxGuests: MAX_PLAYERS, enableVisibilityRecovery: true,
                regenCode: resuming ? undefined : function () { H.code = RC.generateCode(); showCode(); return H.code; },
                onPeerOpen: function () {
                    roomState('ok', t('room_open'));
                    try { RC.markHostedRoom(PREFIX, H.code); } catch (e) { /* storage blocked */ }
                    stage('room_open');
                    persistSoon();
                },
                onGuestConnect: attachConn,
                onError: function (err) {
                    const ty = err && err.type;
                    if (ty === 'unavailable-id' && resuming) {
                        // The old tab's peer still holds the code on the broker for a few seconds.
                        try { H.room.destroy(); } catch (e) { /* already gone */ }
                        if (++tries <= 5) { roomState('wait', t('room_reclaim')); setTimeout(make, 3000); return; }
                        resuming = false; H.code = RC.generateCode(); showCode(); toast(t('room_new_code'), 'warning'); make();
                        return;
                    }
                    const net = ty === 'network' || ty === 'server-error' || ty === 'socket-error';
                    roomState('err', ty === 'peerjs-missing' ? t('net_missing') : net ? t('room_broker_down') : t('room_error', { e: String(ty || 'error') }));
                    diag('host_error', { type: String(ty || '') });
                }
            });
        };
        make();
    }

    function attachConn(conn) {
        const rec = { conn: conn, pid: null, nextPing: 0, burst: 0, lastSeen: performance.now() };
        H.conns.add(rec);
        let stopHb = null;
        try { stopHb = RC.attachHeartbeat(conn, 4000); } catch (e) { stopHb = null; }
        conn.on('data', function (raw) { const h1 = performance.now(); onData(rec, raw, h1); });
        const gone = function () {
            if (!H.conns.has(rec)) return;
            H.conns.delete(rec);
            if (stopHb) stopHb();
            if (rec.pid && !connOf(rec.pid)) eng.setOnline(rec.pid, false);
            scheduleRender();
        };
        conn.on('close', gone);
        conn.on('error', gone);
        send(rec, welcome());
    }

    function welcome() {
        return { t: 'welcome', v: PROTO, teams: eng.teams, scoring: eng.settings.scoring, preset: eng.settings.preset, used: usedColors() };
    }

    function onData(rec, raw, h1) {
        let m = raw;
        if (typeof raw === 'string') { try { m = JSON.parse(raw); } catch (e) { return; } }
        if (!m || typeof m !== 'object') return;
        rec.lastSeen = h1;
        switch (m.t) {
            case 'sq':
                if (rec.pid && typeof m.h === 'number' && typeof m.p === 'number') {
                    const s = H.syncs.get(rec.pid);
                    if (s && s.add(m.h, m.p, h1)) { if (!rec.synced && s.count() >= 3) { rec.synced = true; pushStates(); } scheduleRender(); }
                }
                break;
            case 'hello': onHello(rec, m); break;
            case 'pr': onRemotePress(rec, m, h1); break;
            case 'bye': if (rec.pid) eng.setOnline(rec.pid, false); break;
            default: break;          // 'ka' keep-alives and heartbeats only prove the link
        }
    }

    function onHello(rec, m) {
        const pid = typeof m.pid === 'string' ? m.pid : '';
        if (!/^[a-f0-9]{8,40}$/.test(pid)) return;
        if (H.kicked.has(pid)) { send(rec, { t: 'kick' }); setTimeout(function () { try { rec.conn.close(); } catch (e) { /* gone */ } }, 300); return; }
        H.conns.forEach(function (o) { if (o !== rec && o.pid === pid) { H.conns.delete(o); try { o.conn.close(); } catch (e) { /* stale */ } } });
        const isNew = !eng.players.has(pid);
        if (isNew && eng.players.size >= MAX_PLAYERS) { send(rec, { t: 'full' }); return; }
        rec.pid = pid;
        const cur = eng.players.get(pid);
        let team = validTeam(m.team);
        if (!team && eng.settings.scoring === 'teams') team = (cur && validTeam(cur.team)) || smallestTeam();
        eng.addPlayer({
            pid: pid, name: cleanName(m.name) || (cur && cur.name) || t('player_n', { n: eng.players.size + 1 }),
            color: PALETTE.includes(m.color) ? m.color : (cur ? cur.color : pickColor()),
            team: team, kind: 'remote', online: true
        });
        const boot = String(m.boot || '');
        if (!H.syncs.has(pid) || H.boots.get(pid) !== boot) { H.syncs.set(pid, createClockSync()); H.boots.set(pid, boot); }
        rec.burst = 0; rec.nextPing = 0; rec.synced = false;
        pumpPings();
        if (isNew && !H.firstPlayer) { H.firstPlayer = true; stage('player_joined'); }
        armUnsaved();
        pushStates();
    }

    function onRemotePress(rec, m, h1) {
        if (!rec.pid || !eng.players.has(rec.pid)) return;
        const seq = Number(m.s), p = Number(m.p);
        if (!Number.isFinite(seq) || !Number.isFinite(p)) return;
        eng.setOnline(rec.pid, true);
        const c = correctPress(H.syncs.get(rec.pid), p, h1);
        const r = eng.press({ pid: rec.pid, t: c.t, err: c.err, how: c.how, claimed: c.claimed, seq: seq }, h1);
        send(rec, { t: 'ack', s: seq, k: r.kind });
        if (c.how === 'slow' || c.how === 'future') diag('press_clamped', { how: c.how });
    }

    function pumpPings() {
        const now = performance.now();
        const hot = eng.phase === 'armed';
        H.conns.forEach(function (rec) {
            if (!rec.pid) return;
            if (now - rec.lastSeen > 15000) {
                // Silent for 15 s: the phone slept or walked out of the Wi-Fi.
                H.conns.delete(rec);
                try { rec.conn.close(); } catch (e) { /* gone */ }
                if (!connOf(rec.pid)) eng.setOnline(rec.pid, false);
                return;
            }
            if (now < rec.nextPing) return;
            const s = H.syncs.get(rec.pid);
            const ready = s && s.ready();
            send(rec, { t: 'sp', h: now, o: ready ? s.offset(now) : null, e: ready ? Math.round(s.errAt(now) * 10) / 10 : null });
            rec.burst++;
            rec.nextPing = now + (rec.burst < 8 ? 120 : hot ? 500 : 2000);
        });
    }
    setInterval(pumpPings, 100);

    function pushStates() {
        if (H.pushQueued) return;
        H.pushQueued = true;
        Promise.resolve().then(function () {
            H.pushQueued = false;
            const now = performance.now();
            const top = eng.standings().slice(0, 5).map(function (r) { return { n: r.name, s: r.score, c: r.color, p: r.place }; });
            H.conns.forEach(function (rec) {
                if (!rec.pid) return;
                const s = H.syncs.get(rec.pid);
                const ready = s && s.ready();
                const v = eng.viewFor(rec.pid, now);
                v.top = top;
                v.ended = H.ended;
                const p = eng.players.get(rec.pid);
                if (p) { v.name = p.name; v.color = p.color; }
                send(rec, { t: 'st', v: v, o: ready ? s.offset(now) : null, e: ready ? Math.round(s.errAt(now) * 10) / 10 : null, rtt: ready ? Math.round(s.rttLast()) : null });
            });
            if (H.zones) renderZones();
        });
    }
    function broadcast(msg) { H.conns.forEach(function (rec) { if (rec.pid) send(rec, msg); }); }
    function fxTo(ent, msg) { H.conns.forEach(function (rec) { if (rec.pid && eng.entityOf(rec.pid) === ent) send(rec, msg); }); }

    function armUnsaved() {
        try { if (window.__unsavedWork && eng.players.size) window.__unsavedWork.arm('quiz-buzzer:game'); } catch (e) { /* optional */ }
    }

    // ── persistence (a reloaded host tab resumes the same room and scores) ─
    function persistSoon() {
        clearTimeout(H.persistTimer);
        H.persistTimer = setTimeout(persist, 400);
    }
    function persist() {
        if (H.ended) return;
        store('host', { at: Date.now(), mode: H.mode, code: H.code, game: eng.toJSON(), simultaneous: H.simultaneous,
            muted: snd.isMuted(), volume: Number($('qbVolume') && $('qbVolume').value || 80) / 100 });
    }

    // ── host actions ────────────────────────────────────────────────────────
    function now() { return performance.now(); }
    function act(name) {
        snd.unlock();
        const n = now();
        switch (name) {
            case 'toggle':
                if (eng.phase === 'armed') eng.close(n);
                else if (eng.phase === 'closed' || eng.phase === 'extra') eng.arm(n, leadNow());
                break;
            case 'correct': eng.judge('correct', n, leadNow()); break;
            case 'wrong': eng.judge('wrong', n, leadNow()); break;
            case 'next': {
                const q = eng.question;
                const nx = eng.rankedFirsts().find(function (r) { return !q.locked.has(r.ent) && r.ent !== q.answererEnt; });
                if (nx) eng.pass(nx.pid, n);
                break;
            }
            case 'reset': eng.newQuestion(n); break;
            case 'undo': { const h = eng.undo(); if (!h) toast(t('undo_none')); break; }
            default: break;
        }
        scheduleRender();
    }

    // ── rendering ───────────────────────────────────────────────────────────
    function scheduleRender() {
        if (H.renderQueued) return;
        H.renderQueued = true;
        requestAnimationFrame(function () { H.renderQueued = false; render(); });
    }
    function render() {
        renderStatus(); renderOrder(); renderScores(); renderRoster(); renderLocal(); renderValues(); renderSettings();
        if (H.zones) renderZones();
        loop();
        input.refresh();            // a new gamepad binding starts the pad poll
    }

    function signed(sign, v) { return '\u2066' + sign + num(v) + '\u2069'; }
    function nameOf(pid) { const p = eng.players.get(pid); return p ? p.name : '?'; }
    function entLabel(ent) { return eng.entityInfo(ent).name; }

    function renderStatus() {
        const ph = eng.phase, q = eng.question, n = now();
        const box = $('qbStatus');
        box.dataset.phase = ph;
        const s = eng.settings;
        $('qbQNum').textContent = t('q_n', { n: q.id });
        $('qbStVal').textContent = s.value ? t('q_value', { v: num(s.value) }) : '';
        const main = $('qbStMain'), sub = $('qbStSub');
        main.style.removeProperty('--c');
        let mainText = '', subText = '';
        if (H.ended) { mainText = t('st_ended'); subText = winnersLine(); }
        else if (ph === 'closed') {
            mainText = q.openLeft === 0 ? t('st_timeup') : t('st_closed');
            subText = t('st_closed_sub');
            const pv = eng.previous;
            if (pv && pv.id === q.id - 1 && !q.presses.length && n - pv.endedAt < 120000) subText = verdictLine(pv) + ' · ' + t('st_closed_sub');
            if (q.answerer) { mainText = t('st_answering', { name: nameOf(q.answerer) }); subText = t('st_answering_sub'); }
        } else if (ph === 'armed') {
            if (q.armAt != null && n < q.armAt) { mainText = t('st_ready'); subText = t('st_ready_sub'); }
            else {
                mainText = t('st_open');
                subText = s.collectAll ? t('st_open_collect') : t('st_open_sub');
                if (q.declareAt != null && !q.answerer) subText = t('st_checking', { ms: num(Math.max(0, q.declareAt - n)) });
            }
            if (q.answerer) {
                mainText = t('st_answering', { name: nameOf(q.answerer) });
                main.style.setProperty('--c', (eng.players.get(q.answerer) || {}).color || '');
                subText = t('st_collecting');
            }
        } else if (ph === 'answering') {
            mainText = t('st_answering', { name: nameOf(q.answerer) });
            main.style.setProperty('--c', (eng.players.get(q.answerer) || {}).color || '');
            const rec = eng.rankedFirsts().find(function (r) { return r.pid === q.answerer; });
            subText = rec && rec.rel != null ? t('st_after', { t: secs(rec.rel) }) : t('st_answering_sub');
            if (eng.settings.scoring === 'teams' && q.answererEnt && q.answererEnt.startsWith('t:')) subText = entLabel(q.answererEnt) + ' · ' + subText;
        } else if (ph === 'extra') { mainText = t('st_extra'); subText = t('st_extra_sub'); }
        main.textContent = mainText;
        sub.textContent = subText;

        const open = $('qbOpen');
        open.hidden = ph === 'answering' || !!H.ended;
        open.querySelector('.qb-bl').textContent = ph === 'armed' ? t('btn_close') : t('btn_open');
        open.classList.toggle('ghost', ph === 'armed');
        const has = !!q.answerer;
        $('qbCorrect').disabled = !has;
        $('qbWrong').disabled = !has;
        // Signed numbers are isolated left-to-right, or an Arabic page shows «100−».
        $('qbCorrect').querySelector('.qb-bl').textContent = t('btn_correct') + (s.value ? ' ' + signed('+', s.value) : '');
        $('qbWrong').querySelector('.qb-bl').textContent = t('btn_wrong') + (s.wrongSubtracts && s.value ? ' ' + signed('−', s.value) : '');
        const nx = has && eng.rankedFirsts().some(function (r) { return !q.locked.has(r.ent) && r.ent !== q.answererEnt; });
        $('qbNext').hidden = !nx;
        $('qbEndBanner').hidden = !H.ended;
    }

    function verdictLine(pv) {
        const last = pv.results[pv.results.length - 1];
        if (pv.why === 'correct' && last) return t('v_correct', { name: entLabel(last.ent) });
        if (pv.why === 'all_wrong') return t('v_all_wrong');
        if (pv.why === 'timeout') return t('v_timeout');
        return t('v_reset');
    }
    function winnersLine() {
        const st = eng.standings();
        return st.filter(function (r) { return r.place === 1; }).map(function (r) { return r.name + ' — ' + num(r.score); }).join(', ');
    }

    function renderOrder() {
        const list = $('qbOrder');
        let q = eng.question, faded = false;
        if (!q.presses.length && eng.previous && eng.phase === 'closed' && !q.answerer) { q = eng.previous; faded = true; }
        list.textContent = '';
        list.classList.toggle('qb-faded', faded);
        if (!faded) eng.rankedFirsts();             // refreshes rank / gap / tie on the live question
        const round = q.round;
        const valid = q.presses.filter(function (r) { return r.kind === 'valid' && r.round === round; })
            .sort(function (a, b) { return a.t - b.t || a.recvAt - b.recvAt; });
        const seenEnt = new Set();
        let lead = null;
        valid.forEach(function (r) {
            const li = el('li', 'qb-row');
            const first = !seenEnt.has(r.ent);
            seenEnt.add(r.ent);
            if (!lead) lead = r;
            li.dataset.kind = first ? 'valid' : 'mate';
            if (r.pid === q.answerer) li.classList.add('qb-ans');
            if (q.locked.get(r.ent) === 'wrong') li.classList.add('qb-wrongd');
            li.appendChild(el('span', 'qb-rk', first ? String(r.rank || '') : '·'));
            li.appendChild(nameCell(r));
            li.appendChild(el('span', 'qb-tm', r.rel != null ? secs(r.rel) : ''));
            li.appendChild(el('span', 'qb-gp', r === lead ? '' : '\u2066+' + msec(r.t - lead.t) + '\u2069'));
            const fl = el('span', 'qb-fl');
            if (r.tie && first) fl.appendChild(flag('≈', t('flag_tie', { e: msec(Math.max(r.err || 0, 1)) }), 'tie'));
            if (r.late) fl.appendChild(flag('⚠', t('flag_late'), 'late'));
            if (r.how === 'slow' || r.how === 'future' || r.how === 'unsynced') fl.appendChild(flag('⏱', t('flag_arrival'), 'arr'));
            if (q.locked.get(r.ent) === 'wrong') fl.appendChild(flag('✗', t('flag_wrong'), 'wrong'));
            li.appendChild(fl);
            if (!faded && first && r.pid !== q.answerer && !q.locked.has(r.ent)) {
                const b = el('button', 'tc-btn ghost sm qb-pass', t('btn_pass'));
                b.type = 'button'; b.dataset.pass = r.pid;
                li.appendChild(b);
            }
            list.appendChild(li);
        });
        q.presses.filter(function (r) { return r.kind !== 'valid' || r.round !== round; }).slice(-12).forEach(function (r) {
            if (r.kind === 'valid') return;             // earlier rounds: already judged
            const li = el('li', 'qb-row qb-row-x');
            li.dataset.kind = r.kind;
            li.appendChild(el('span', 'qb-rk', '—'));
            li.appendChild(nameCell(r));
            let what = t('k_' + r.kind);
            if (r.kind === 'false' && r.rel != null) what += ' (' + secs(r.rel) + ')';
            li.appendChild(el('span', 'qb-tm qb-kind', what));
            li.appendChild(el('span', 'qb-gp', ''));
            li.appendChild(el('span', 'qb-fl', ''));
            if (!faded && (r.kind === 'false' || r.kind === 'out' || r.kind === 'locked')) {
                const b = el('button', 'tc-btn ghost sm qb-pass', t('btn_unlock'));
                b.type = 'button'; b.dataset.unlock = r.pid;
                li.appendChild(b);
            }
            list.appendChild(li);
        });
        $('qbOrderEmpty').hidden = list.children.length > 0;
        $('qbOrderTitle').textContent = faded ? t('order_prev') : t('order_title');
        const g = graceFor(remoteSyncs());
        $('qbGrace').textContent = g ? t('grace_note', { ms: num(g) }) : '';
    }
    function nameCell(r) {
        const p = eng.players.get(r.pid);
        const s = el('span', 'qb-nm');
        const dot = el('i', 'qb-dot');
        dot.style.setProperty('--c', p ? p.color : '#888');
        s.appendChild(dot);
        s.appendChild(el('b', null, p ? p.name : '?'));
        if (r.ent && r.ent.startsWith('t:')) s.appendChild(el('small', null, entLabel(r.ent)));
        return s;
    }
    function flag(txt, title, kind) {
        const f = el('span', 'qb-flag qb-flag-' + kind, txt);
        f.title = title;
        f.setAttribute('aria-label', title);
        return f;
    }

    function renderScores() {
        const box = $('qbScores');
        const st = eng.standings();
        box.textContent = '';
        const v = Number(eng.settings.value) || 1;
        st.forEach(function (r) {
            const row = el('div', 'qb-sc');
            row.appendChild(el('span', 'qb-sc-pl', String(r.place)));
            const nm = el('span', 'qb-sc-nm');
            const dot = el('i', 'qb-dot'); dot.style.setProperty('--c', r.color); nm.appendChild(dot);
            nm.appendChild(el('span', null, r.name));
            if (!r.team) {
                const p = eng.players.get(r.ent.slice(2));
                if (p && p.kind === 'remote' && p.online === false) nm.appendChild(el('small', 'qb-off', t('offline')));
            }
            row.appendChild(nm);
            row.appendChild(el('b', 'qb-sc-v', num(r.score)));
            const minus = el('button', 'tc-btn ghost sm qb-sc-b', signed('−', v));
            minus.type = 'button'; minus.dataset.adj = r.ent; minus.dataset.d = String(-v);
            minus.setAttribute('aria-label', t('adj_minus', { name: r.name, v: num(v) }));
            const plus = el('button', 'tc-btn ghost sm qb-sc-b', signed('+', v));
            plus.type = 'button'; plus.dataset.adj = r.ent; plus.dataset.d = String(v);
            plus.setAttribute('aria-label', t('adj_plus', { name: r.name, v: num(v) }));
            row.appendChild(minus); row.appendChild(plus);
            box.appendChild(row);
        });
        $('qbScoresEmpty').hidden = st.length > 0;
    }

    function renderRoster() {
        const ul = $('qbRoster');
        ul.textContent = '';
        let online = 0, total = 0;
        const n = now();
        eng.players.forEach(function (p, pid) {
            if (p.kind !== 'remote') return;
            total++;
            if (p.online !== false) online++;
            const li = el('li', 'qb-ros' + (p.online === false ? ' qb-ros-off' : ''));
            const dot = el('i', 'qb-dot'); dot.style.setProperty('--c', p.color);
            li.appendChild(dot);
            const nm = el('span', 'qb-ros-nm', p.name);
            li.appendChild(nm);
            const s = H.syncs.get(pid);
            let net;
            if (p.online === false) net = t('offline');
            else if (!s || !s.ready()) net = t('net_sync');
            else net = t('net_ok', { rtt: msec(s.rttLast()), err: msec(s.errAt(n)) });
            const ns = el('span', 'qb-ros-net', net);
            if (s && s.ready() && s.errAt(n) > 40) ns.classList.add('qb-warn');
            li.appendChild(ns);
            if (eng.settings.scoring === 'teams' && eng.teams.length) li.appendChild(teamSelect(pid, p.team));
            const x = el('button', 'qb-x', '×');
            x.type = 'button'; x.dataset.kick = pid;
            x.setAttribute('aria-label', t('kick', { name: p.name }));
            x.title = t('kick', { name: p.name });
            li.appendChild(x);
            ul.appendChild(li);
        });
        $('qbRosterEmpty').hidden = total > 0;
        $('qbPlayersSub').textContent = total ? t('players_online', { n: online, total: total }) : '';
        $('qbRoomSub').textContent = H.code ? H.code.toUpperCase() : '';
    }
    function teamSelect(pid, cur) {
        const sel = el('select', 'tc-in qb-team-sel');
        sel.dataset.team = pid;
        sel.setAttribute('aria-label', t('team_of', { name: nameOf(pid) }));
        eng.teams.forEach(function (tm) { const o = el('option', null, tm.name); o.value = tm.id; if (tm.id === cur) o.selected = true; sel.appendChild(o); });
        return sel;
    }

    function renderLocal() {
        const ul = $('qbLocalList');
        const focusId = document.activeElement && document.activeElement.dataset ? document.activeElement.dataset.lname : null;
        if (focusId) return;                       // do not rebuild the row someone is typing in
        ul.textContent = '';
        localPlayers().forEach(function (p) {
            const li = el('li', 'qb-loc');
            const sw = el('button', 'qb-sw');
            sw.type = 'button'; sw.dataset.color = p.pid;
            sw.style.setProperty('--c', p.color);
            sw.setAttribute('aria-label', t('color_change', { name: p.name }));
            li.appendChild(sw);
            const inp = el('input', 'tc-in qb-loc-nm');
            inp.value = p.name; inp.maxLength = 24; inp.dataset.lname = p.pid;
            inp.setAttribute('aria-label', t('name_label'));
            li.appendChild(inp);
            const kb = el('button', 'tc-btn ghost sm qb-key');
            kb.type = 'button'; kb.dataset.learn = p.pid;
            const k = p.key ? (String(p.key).indexOf('pad:') === 0 ? { pad: String(p.key).slice(4) } : { key: p.key }) : null;
            kb.textContent = H.learning === p.pid ? t('key_press_now') : (k ? keyLabel(k, t('pad')) : t('key_none'));
            if (H.learning === p.pid) kb.classList.add('qb-learning');
            kb.setAttribute('aria-label', t('key_assign', { name: p.name }));
            li.appendChild(kb);
            if (eng.settings.scoring === 'teams' && eng.teams.length) li.appendChild(teamSelect(p.pid, p.team));
            const x = el('button', 'qb-x', '×');
            x.type = 'button'; x.dataset.lremove = p.pid;
            x.setAttribute('aria-label', t('remove', { name: p.name }));
            li.appendChild(x);
            ul.appendChild(li);
            if (p.key && HOST_KEYS.includes(p.key)) {
                const w = el('li', 'qb-loc-warn', t('key_taken_host', { key: keyLabel(k) }));
                ul.appendChild(w);
            }
        });
        $('qbLocalEmpty').hidden = localPlayers().length > 0;
    }

    function renderValues() {
        const s = eng.settings;
        const chips = $('qbValChips');
        chips.textContent = '';
        const base = Number(s.base) || 1, round = Number(s.round) || 1;
        for (let k = 1; k <= 5; k++) {
            const v = base * round * k;
            const b = el('button', 'tc-chip' + (Number(s.value) === v ? ' on' : ''), num(v));
            b.type = 'button'; b.dataset.value = String(v);
            b.setAttribute('aria-pressed', Number(s.value) === v ? 'true' : 'false');
            b.title = t('value_key', { k: k });
            chips.appendChild(b);
        }
        const vi = $('qbValue');
        if (document.activeElement !== vi) vi.value = String(s.value);
        $('qbRoundRow').hidden = s.preset !== 'jeopardy';
        document.querySelectorAll('#qbRound .tc-seg-b').forEach(function (b) { b.classList.toggle('on', Number(b.dataset.round) === round); b.setAttribute('aria-pressed', Number(b.dataset.round) === round ? 'true' : 'false'); });
    }

    function renderSettings() {
        const s = eng.settings;
        document.querySelectorAll('#qbPresetSeg .tc-seg-b').forEach(function (b) { const on = b.dataset.preset === s.preset; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        document.querySelectorAll('#qbScoringSeg .tc-seg-b').forEach(function (b) { const on = b.dataset.scoring === s.scoring; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        setSel('qbFalse', s.falseStart === 'lockout' ? 'lock' + s.lockoutMs : s.falseStart);
        setSel('qbOpenSec', String(s.openSec || 0));
        setSel('qbAnswerSec', String(s.answerSec || 0));
        setSel('qbAfterWrong', s.afterWrong);
        setSel('qbWarn', String(s.warnSec || 0));
        setSel('qbExtra', String(s.extraSec || 0));
        $('qbWrongSub').checked = !!s.wrongSubtracts;
        $('qbCollect').checked = !!s.collectAll;
        $('qbSimul').checked = H.simultaneous;
        $('qbTeamsBox').hidden = s.scoring !== 'teams';
        const tl = $('qbTeamList');
        if (!(document.activeElement && document.activeElement.dataset && document.activeElement.dataset.tname)) {
            tl.textContent = '';
            eng.teams.forEach(function (tm) {
                const li = el('li', 'qb-loc');
                const sw = el('button', 'qb-sw'); sw.type = 'button'; sw.dataset.tcolor = tm.id; sw.style.setProperty('--c', tm.color);
                sw.setAttribute('aria-label', t('color_change', { name: tm.name }));
                li.appendChild(sw);
                const inp = el('input', 'tc-in qb-loc-nm'); inp.value = tm.name; inp.maxLength = 24; inp.dataset.tname = tm.id;
                inp.setAttribute('aria-label', t('team_name'));
                li.appendChild(inp);
                const x = el('button', 'qb-x', '×'); x.type = 'button'; x.dataset.tremove = tm.id;
                x.setAttribute('aria-label', t('remove', { name: tm.name }));
                li.appendChild(x);
                tl.appendChild(li);
            });
        }
        $('qbSetSub').textContent = t('preset_' + s.preset);
        $('qbSound').textContent = snd.isMuted() ? '🔇' : '🔊';
        $('qbSound').setAttribute('aria-label', snd.isMuted() ? t('sound_on') : t('sound_off'));
    }
    function setSel(id, v) { const s = $(id); if (s && document.activeElement !== s) { if (Array.from(s.options).some(function (o) { return o.value === v; })) s.value = v; } }

    // ── the countdown loop (only while something is counting) ──────────────
    function loop() {
        if (H.raf) return;
        const step = function () {
            H.raf = 0;
            const n = now();
            const tm = eng.timer, q = eng.question;
            const box = $('qbStTimer');
            let running = false;
            if (tm && !H.ended) {
                const from = tm.kind === 'open' ? Math.max(q.armAt || n, n) : n;
                const left = Math.max(0, tm.endsAt - from);
                box.hidden = false;
                box.dataset.kind = tm.kind;
                box.querySelector('span').textContent = clock(left);
                const bar = box.querySelector('i');
                bar.style.width = (tm.total ? Math.min(100, left / tm.total * 100) : 0) + '%';
                const sec = Math.ceil(left / 1000);
                if (left > 0 && sec <= 5 && sec !== H.lastTickSec && (tm.kind !== 'open' || n >= (q.armAt || 0))) { H.lastTickSec = sec; if (sec < 5 || tm.kind !== 'open' || !eng.settings.warnSec) snd.play('tick'); }
                if (sec > 5) H.lastTickSec = null;
                running = left > 0;
            } else {
                box.hidden = true;
            }
            if (eng.phase === 'armed' && q.armAt != null && n < q.armAt) running = true;
            if (eng.phase === 'armed' && q.declareAt != null) running = true;
            if (running) {
                renderStatusLight();
                H.raf = requestAnimationFrame(step);
            }
        };
        H.raf = requestAnimationFrame(step);
    }
    let lastPhaseKey = '';
    function renderStatusLight() {
        const q = eng.question, n = now();
        const key = eng.phase + (q.armAt != null && n < q.armAt ? 'L' : '') + (q.declareAt != null ? 'D' : '') + (q.answerer || '');
        if (key !== lastPhaseKey) { lastPhaseKey = key; renderStatus(); pushStates(); }
    }

    // ── touch zones: 2–4 players on one tablet ──────────────────────────────
    function openZones() {
        let locals = localPlayers();
        while (locals.length < 2) { addLocalPlayer(); locals = localPlayers(); }
        H.zones = true;
        const z = $('qbZones');
        z.hidden = false;
        document.documentElement.classList.add('qb-noscroll');
        if (window.__safeFullscreen) window.__safeFullscreen(z);
        snd.unlock();
        renderZones();
        stage('zones');
    }
    function closeZones() {
        H.zones = false;
        $('qbZones').hidden = true;
        document.documentElement.classList.remove('qb-noscroll');
        try { if (document.fullscreenElement === $('qbZones')) document.exitFullscreen(); } catch (e) { /* not fullscreen */ }
    }
    function renderZones() {
        const grid = $('qbZoneGrid');
        const locals = localPlayers().slice(0, 4);
        grid.dataset.n = String(locals.length);
        const n = now();
        while (grid.children.length > locals.length) grid.removeChild(grid.lastChild);
        locals.forEach(function (p, i) {
            let z = grid.children[i];
            if (!z) {
                z = el('div', 'qb-zone');
                z.setAttribute('role', 'button');
                z.tabIndex = -1;
                z.appendChild(el('span', 'qb-zone-nm'));
                z.appendChild(el('span', 'qb-zone-st'));
                z.appendChild(el('span', 'qb-zone-sc'));
                grid.appendChild(z);
            }
            z.dataset.pid = p.pid;
            z.style.setProperty('--c', p.color);
            z.style.setProperty('--ink', inkOn(p.color));
            const v = eng.viewFor(p.pid, n);
            z.dataset.state = v.state;
            z.children[0].textContent = p.name;
            z.children[1].textContent = zoneText(v);
            z.children[2].textContent = num(v.score);
            z.setAttribute('aria-label', p.name + ': ' + zoneText(v));
        });
        const b = $('qbZOpen');
        b.textContent = eng.phase === 'armed' ? t('btn_close') : t('btn_open');
        b.disabled = eng.phase === 'answering';
        $('qbZOk').disabled = !eng.question.answerer;
        $('qbZBad').disabled = !eng.question.answerer;
    }
    function zoneText(v) {
        switch (v.state) {
            case 'armed': return t('pl_armed');
            case 'pressed': return t('pl_pressed');
            case 'first': case 'team_first': return t('pl_first');
            case 'rank': return t('pl_rank', { n: v.rank, gap: msec(v.gap) }).split('+' + msec(v.gap)).join('\u2066+' + msec(v.gap) + '\u2069');
            case 'penalty': return t('pl_penalty');
            case 'out': return v.reason === 'false' ? t('pl_out_false') : t('pl_out');
            case 'wait': return t('pl_wait_other', { name: v.answerer || '' });
            case 'extra': return t('pl_extra');
            default: return t('pl_closed');
        }
    }

    // ── projector ───────────────────────────────────────────────────────────
    function setPresent(on) {
        H.present = !!on;
        root.classList.toggle('qb-present', H.present);
        $('qbPresent').setAttribute('aria-pressed', H.present ? 'true' : 'false');
        if (H.present) {
            if (window.__safeFullscreen) window.__safeFullscreen(root);
            funnel('present_on');
        } else {
            try { if (document.fullscreenElement === root) document.exitFullscreen(); } catch (e) { /* not fullscreen */ }
        }
    }
    document.addEventListener('fullscreenchange', function () {
        if (H.present && document.fullscreenElement !== root) setPresent(false);
        if (H.zones && document.fullscreenElement !== $('qbZones') && !document.fullscreenElement) { /* zones stay open without fullscreen */ }
    });

    // ── results ─────────────────────────────────────────────────────────────
    function resultsText() {
        return eng.standings().map(function (r) { return r.place + '. ' + r.name + ' — ' + num(r.score); }).join('\n');
    }
    function resultsCsv() {
        const esc = function (s) { s = String(s); return /[",\n;]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
        const rows = [[t('csv_place'), t('csv_name'), t('csv_score')]];
        eng.standings().forEach(function (r) { rows.push([r.place, r.name, r.score]); });
        return '\uFEFF' + rows.map(function (r) { return r.map(esc).join(','); }).join('\r\n');
    }
    function endGame() {
        H.ended = true;
        broadcast({ t: 'end', top: eng.standings().slice(0, 10).map(function (r) { return { n: r.name, s: r.score, c: r.color, p: r.place }; }) });
        pushStates();
        try { if (window.__unsavedWork) window.__unsavedWork.disarm(); } catch (e) { /* optional */ }
        store('host', null);
        try { if (H.code && RC) RC.clearHostedRoom(PREFIX, H.code); } catch (e) { /* storage */ }
        funnel('game_end', { players: eng.players.size, questions: eng.question.id - 1 });
        render();
    }
    function newGame() {
        H.ended = false;
        eng.resetScores();
        eng.newQuestion(now());
        broadcast({ t: 'restart' });
        persistSoon();
        render();
    }

    // ── wiring ──────────────────────────────────────────────────────────────
    function on(id, ev, fn) { const e = $(id); if (e) e.addEventListener(ev, fn); }
    on('qbOpen', 'click', function () { act('toggle'); });
    on('qbCorrect', 'click', function () { act('correct'); });
    on('qbWrong', 'click', function () { act('wrong'); });
    on('qbNext', 'click', function () { act('next'); });
    on('qbReset', 'click', function () { act('reset'); });
    on('qbUndo', 'click', function () { act('undo'); });
    on('qbPresent', 'click', function () { setPresent(!H.present); });
    on('qbPresentExit', 'click', function () { setPresent(false); });
    on('qbSound', 'click', function () { snd.setMuted(!snd.isMuted()); if (!snd.isMuted()) { snd.unlock(); snd.play('press'); } renderSettings(); persistSoon(); });
    on('qbVolume', 'input', function () { snd.setVolume(Number($('qbVolume').value) / 100); persistSoon(); });
    on('qbCopy', 'click', function () { copy($('qbLink').value, t('copied_link')); });
    on('qbCopyCode', 'click', function () { copy(H.code ? H.code.toUpperCase() : '', t('copied_code')); });
    on('qbResCopy', 'click', function () { copy(resultsText(), t('copied_results')); });
    on('qbResCsv', 'click', function () {
        const d = new Date();
        const name = 'quiz-results-' + d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') + '.csv';
        const blob = new Blob([resultsCsv()], { type: 'text/csv;charset=utf-8' });
        if (window.__downloadFile) window.__downloadFile(blob, name);
    });
    on('qbEnd', 'click', function () { if (window.confirm(t('end_confirm'))) endGame(); });
    on('qbNewGame', 'click', function () { if (H.ended || window.confirm(t('newgame_confirm'))) newGame(); });
    on('qbEndNew', 'click', function () { newGame(); });
    on('qbAddLocal', 'click', function () { addLocalPlayer(); render(); });
    on('qbZonesBtn', 'click', openZones);
    on('qbZClose', 'click', closeZones);
    on('qbZOpen', 'click', function () { act('toggle'); });
    on('qbZOk', 'click', function () { act('correct'); });
    on('qbZBad', 'click', function () { act('wrong'); });
    on('qbZReset', 'click', function () { act('reset'); });
    on('qbAddTeam', 'click', function () {
        const tm = eng.teams;
        if (tm.length >= 8) return;
        tm.push({ id: rid(3), name: t('team_n', { n: tm.length + 1 }), color: TEAM_COLORS[tm.length % TEAM_COLORS.length] });
        eng.setTeams(tm);
        broadcastCfg();
    });
    on('qbValue', 'change', function () {
        const v = Math.round(Number($('qbValue').value));
        if (Number.isFinite(v) && v >= 0 && v <= 1e6) eng.configure({ value: v });
    });

    // Delegated clicks inside lists.
    root.addEventListener('click', function (e) {
        const b = e.target.closest ? e.target.closest('button') : null;
        if (!b || !root.contains(b)) return;
        const d = b.dataset;
        if (d.pass) { eng.pass(d.pass, now()); }
        else if (d.unlock) { eng.unlock(d.unlock); }
        else if (d.adj) { eng.adjust(d.adj, Number(d.d)); }
        else if (d.value) { eng.configure({ value: Number(d.value) }); }
        else if (d.round) { const r = Number(d.round); eng.configure({ round: r, value: (Number(eng.settings.base) || 1) * r }); }
        else if (d.preset) { applyPreset(d.preset); }
        else if (d.scoring) { setScoring(d.scoring); }
        else if (d.kick) { kick(d.kick); }
        else if (d.lremove) { eng.removePlayer(d.lremove); }
        else if (d.learn) { learnKey(d.learn); }
        else if (d.color) { cycleColor(d.color); }
        else if (d.tcolor) { cycleTeamColor(d.tcolor); }
        else if (d.tremove) { eng.setTeams(eng.teams.filter(function (x) { return x.id !== d.tremove; })); broadcastCfg(); }
        else return;
        scheduleRender();
    });
    root.addEventListener('change', function (e) {
        const x = e.target;
        if (!x || !x.dataset) return;
        if (x.dataset.team) { const p = eng.players.get(x.dataset.team); if (p) { eng.addPlayer(Object.assign({}, p, { team: x.value })); pushStates(); } }
        else if (x.dataset.lname) { const p = eng.players.get(x.dataset.lname); if (p) eng.addPlayer(Object.assign({}, p, { name: cleanName(x.value) || p.name })); x.blur(); }
        else if (x.dataset.tname) {
            const tm = eng.teams.map(function (y) { return y.id === x.dataset.tname ? Object.assign({}, y, { name: cleanName(x.value) || y.name }) : y; });
            x.blur(); eng.setTeams(tm); broadcastCfg();
        }
    });
    root.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && e.target && e.target.dataset && (e.target.dataset.lname || e.target.dataset.tname)) { e.preventDefault(); e.target.blur(); }
    });
    root.addEventListener('focusout', function (e) {
        const x = e.target;
        if (x && x.dataset && (x.dataset.lname || x.dataset.tname)) setTimeout(scheduleRender, 0);
    });

    function bindSel(id, fn) { on(id, 'change', function () { fn($(id).value); }); }
    bindSel('qbFalse', function (v) {
        if (v.indexOf('lock') === 0) eng.configure({ falseStart: 'lockout', lockoutMs: Number(v.slice(4)) });
        else eng.configure({ falseStart: v, lockoutMs: 0 });
    });
    bindSel('qbOpenSec', function (v) { eng.configure({ openSec: Number(v) }); });
    bindSel('qbAnswerSec', function (v) { eng.configure({ answerSec: Number(v) }); });
    bindSel('qbAfterWrong', function (v) { eng.configure({ afterWrong: v }); });
    bindSel('qbWarn', function (v) { eng.configure({ warnSec: Number(v) }); });
    bindSel('qbExtra', function (v) { eng.configure({ extraSec: Number(v) }); });
    on('qbWrongSub', 'change', function () { eng.configure({ wrongSubtracts: $('qbWrongSub').checked }); });
    on('qbCollect', 'change', function () { eng.configure({ collectAll: $('qbCollect').checked }); });
    on('qbSimul', 'change', function () { H.simultaneous = $('qbSimul').checked; persistSoon(); });

    function applyPreset(name) {
        if (!PRESETS[name]) return;
        eng.applyPreset(name);
        if (eng.settings.scoring === 'teams' && !eng.teams.length) eng.setTeams(defaultTeams(2));
        if (eng.settings.scoring === 'teams') assignTeamless();
        broadcastCfg();
        funnel('preset', { preset: name });
    }
    function setScoring(v) {
        eng.configure({ scoring: v });
        if (v === 'teams' && !eng.teams.length) eng.setTeams(defaultTeams(2));
        if (v === 'teams') assignTeamless();
        broadcastCfg();
    }
    function assignTeamless() {
        eng.players.forEach(function (p) { if (!validTeam(p.team)) eng.addPlayer(Object.assign({}, p, { team: smallestTeam() })); });
    }
    function broadcastCfg() { broadcast({ t: 'cfg', teams: eng.teams, scoring: eng.settings.scoring }); pushStates(); }
    function kick(pid) {
        const p = eng.players.get(pid);
        if (!p || !window.confirm(t('kick_confirm', { name: p.name }))) return;
        H.kicked.add(pid);
        const rec = connOf(pid);
        if (rec) { send(rec, { t: 'kick' }); setTimeout(function () { try { rec.conn.close(); } catch (e) { /* gone */ } }, 300); }
        eng.removePlayer(pid);
    }
    function cycleColor(pid) {
        const p = eng.players.get(pid);
        if (!p) return;
        const i = PALETTE.indexOf(p.color);
        eng.addPlayer(Object.assign({}, p, { color: PALETTE[(i + 1) % PALETTE.length] }));
    }
    function cycleTeamColor(id) {
        eng.setTeams(eng.teams.map(function (x) { if (x.id !== id) return x; const i = TEAM_COLORS.indexOf(x.color); return Object.assign({}, x, { color: TEAM_COLORS[(i + 1) % TEAM_COLORS.length] }); }));
        broadcastCfg();
    }
    function learnKey(pid) {
        if (H.learning === pid) { input.cancelLearn(); return; }
        H.learning = pid;
        renderLocal();
        input.learn(pid).then(function (b) {
            H.learning = null;
            if (b) {
                const p = eng.players.get(pid);
                const code = b.pad ? 'pad:' + b.pad : b.key;
                // One key, one player: whoever had it loses it.
                localPlayers().forEach(function (o) { if (o.pid !== pid && o.key === code) eng.addPlayer(Object.assign({}, o, { key: null })); });
                if (p) eng.addPlayer(Object.assign({}, p, { key: code }));
                funnel('key_bound', { pad: b.pad ? 1 : 0 });
            }
            scheduleRender();
        });
    }
    function copy(text, okMsg) {
        if (!text) return;
        const done = function () { toast(okMsg, 'success'); };
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, function () { toast(text); });
            else toast(text);
        } catch (e) { toast(text); }
    }

    // Host keyboard: after the players' keys (inputs.js takes those in capture
    // and prevents default), and never while typing into a field.
    document.addEventListener('keydown', function (e) {
        if (root.hidden) return;
        if (e.defaultPrevented || editable(e.target) || input.isLearning()) return;
        if (e.altKey || e.metaKey) return;
        if (e.ctrlKey) { if (e.code === 'KeyZ') { e.preventDefault(); act('undo'); } return; }
        if (e.repeat) return;
        const c = e.code;
        let handled = true;
        if (c === 'Space') act('toggle');
        else if (c === 'Enter' || c === 'NumpadEnter') act('correct');
        else if (c === 'Backspace' || c === 'Delete') act('wrong');
        else if (c === 'KeyN') act('next');
        else if (c === 'KeyR') act('reset');
        else if (c === 'KeyZ') act('undo');
        else if (c === 'KeyF') setPresent(!H.present);
        else if (c === 'KeyM') { snd.setMuted(!snd.isMuted()); renderSettings(); }
        else if (c === 'Escape') { if (H.zones) closeZones(); else if (H.present) setPresent(false); else handled = false; }
        else if (/^Digit[1-5]$/.test(c)) {
            const k = Number(c.slice(5));
            eng.configure({ value: (Number(eng.settings.base) || 1) * (Number(eng.settings.round) || 1) * k });
        } else handled = false;
        if (handled) { e.preventDefault(); scheduleRender(); }
    });

    // Tap anywhere in a zone buzzes that player; multi-touch is fine.
    $('qbZoneGrid').addEventListener('pointerdown', function (e) {
        const z = e.target.closest ? e.target.closest('.qb-zone') : null;
        if (!z || !z.dataset.pid) return;
        e.preventDefault();
        localPress(z.dataset.pid, evStamp(e), 'touch');
        z.classList.remove('qb-hit'); void z.offsetWidth; z.classList.add('qb-hit');
    });
    $('qbZoneGrid').addEventListener('contextmenu', function (e) { e.preventDefault(); });

    // ── go ──────────────────────────────────────────────────────────────────
    root.hidden = false;
    root.dataset.mode = H.mode;
    $('qbRoomDrawer').hidden = H.mode !== 'phones';
    $('qbPlayersDrawer').hidden = H.mode !== 'phones';
    if (H.mode === 'local') $('qbLocalDrawer').open = true;
    $('qbPresentJoin').hidden = H.mode !== 'phones';
    try { if (window.__wakeLock) window.__wakeLock.request(); } catch (e) { /* optional */ }
    snd.unlock();
    if (H.mode === 'phones') {
        const code = (saved && saved.code && RC && RC.validateCode(saved.code)) || (RC ? RC.generateCode() : '');
        openRoom(code, !!(saved && saved.code));
    }
    stage(H.mode === 'phones' ? 'host_phones' : 'host_local', { preset: eng.settings.preset });
    armUnsaved();
    render();
    persistSoon();
    // Let the rest of the page (tests, other widgets) see the referee.
    window.__qbHost = { engine: eng, H: H, act: act, syncs: H.syncs };
    return window.__qbHost;
}
